
/// <reference types="@types/dom-mediacapture-record" />
import Button from '@/components/Button.vue';
import TermsAndConditionsCheck from '@/components/TermsAndConditionsCheck.vue';
import { AppFreeAnswerParameters, Client, Question } from 'altru-types';
import { computed, onBeforeUnmount, onMounted, PropType, Ref, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { permissionDeniedMessage, SetupProps, SetupReturnType, VideoLoadError } from '@/types/desktop';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import Timer from 'easytimer.js';
import GuidelineOverlay from '@/components/GuidelineOverlay.vue';
import CircularProgress from '@/components/CircularProgress.vue';
import isAcceptedVideoType, { ACCEPTED_TYPES, USER_READABLE_ACCEPTED_TYPES } from '@/utils/isAcceptedVideoType';
import { getConnectedDevices, getUserMedia, hasGetUserMedia, revokeMediaAccess } from '@/utils/userMedia';
import VideoPlaceholder from '@/components/VideoPlaceholder.vue';

export default {
  name: 'Desktop',
  props: {
    uploadVideo: {
      type: Function as PropType<(video: File) => void>,
    },
  },
  components: {
    VideoPlaceholder,
    CircularProgress,
    GuidelineOverlay,
    Button,
    TermsAndConditionsCheck,
  },
  setup(props: SetupProps): SetupReturnType {
    const { t } = useI18n();
    const { getters, dispatch } = useStore();
    const userInformationNeeded: Ref<boolean> = computed(() => getters['clients/userInformationNeeded']);
    const questionInfo: Ref<Question> = computed(() => getters['questions/questionInfo']);
    const isIframed: Ref<boolean> = computed(() => getters['status/isIframed']);
    const iframeSource: Ref<AppFreeAnswerParameters['source']> = computed(() => getters['status/iframeSource']);
    const formattedVideoMaxDuration: Ref<string> | string = computed(() =>
      questionInfo.value.max_duration
        ? new Date(questionInfo.value.max_duration * 1000).toISOString().substr(14, 5)
        : '00:00',
    );
    const isLoading: Ref<boolean> = computed(() => getters['ui/isLoading']);
    const showGuidelines: Ref<boolean> = ref(true);
    const iframeParentIsAdminApp: Ref<boolean> = computed(() => getters['status/iframeParentIsAdminApp']);
    const client: Ref<Client | null> = computed(() => getters['clients/workspaceInfo']);
    const clickWrapFeatureEnabled: Ref<boolean> = computed(
      () => client.value?.feature_keys?.includes('click_wrap_terms_and_conditions') ?? false,
    );

    const videoContainer: Ref<HTMLDivElement | null> = ref<HTMLDivElement | null>(null);
    const desktopVideoElement: Ref<HTMLVideoElement | null> = ref<HTMLVideoElement | null>(null);
    const desktopRecordedVideoElement: Ref<HTMLVideoElement | null> = ref<HTMLVideoElement | null>(null);
    const downloadableRecordedVideoElement: Ref<HTMLAnchorElement | null> = ref<HTMLAnchorElement | null>(null);

    const videoInputDevices: Ref<{ deviceId: string; label: string }[]> = ref([]);
    const audioInputDevices: Ref<{ deviceId: string; label: string }[]> = ref([]);
    const selectedVideoDevice: Ref<string> = ref('');
    const selectedAudioDevice: Ref<string> = ref('');

    const countDown: Ref<number> = ref(-1);
    const startButtonVisible: Ref<boolean> = ref(true);
    const resumeButtonVisible: Ref<boolean> = ref(false);
    const restartButtonVisible: Ref<boolean> = ref(false);

    const videoPreviewVisible: Ref<boolean> = ref(false);
    const isPaused: Ref<boolean> = ref(false);
    const isRecording: Ref<boolean> = ref(false);
    const mediaRecorder: Ref<MediaRecorder | undefined> = ref(undefined);
    const defaultRecordingState: RecordingState = 'inactive';
    const recordingStatus: Ref<RecordingState> = ref(defaultRecordingState);
    const chunks: Ref<any> = ref([]);
    const videoLoaded = ref(false);
    const videoLoadError: VideoLoadError = ref(null);
    const blob: Ref<Blob | null> = ref(new Blob());
    const attachedFile: Ref<File | null> = ref(null);
    const timer = new Timer();
    const elapsedTime: Ref<string> = ref('00:00');
    const progress: Ref<number> = ref(0);

    onMounted(async () => await initializeUserMedia());

    onBeforeUnmount(() => revokeMediaAccess());

    function handleError(error: any) {
      videoLoadError.value = {
        title: 'Error',
        description: error ? error : permissionDeniedMessage,
      };
      console.error('Error: ', error ?? permissionDeniedMessage);
    }

    const attachStreamToVideoElement = (stream: MediaStream): void => {
      if (window) {
        (window as any).stream = stream; // make stream available to console
      }

      selectedVideoDevice.value = selectedVideoDevice.value ?? videoInputDevices.value[0]?.deviceId;
      selectedAudioDevice.value = selectedAudioDevice.value ?? audioInputDevices.value[0]?.deviceId;

      mediaRecorder.value = new MediaRecorder(stream);
      recordingStatus.value = mediaRecorder.value?.state;

      if (desktopVideoElement.value) {
        if ('srcObject' in desktopVideoElement.value) {
          desktopVideoElement.value.srcObject = stream;
          videoLoaded.value = true;
        } else {
          (desktopVideoElement.value as HTMLVideoElement).src = window.URL.createObjectURL(stream);
        }
      }

      if (mediaRecorder.value) {
        mediaRecorder.value.ondataavailable = (ev: any) => {
          chunks.value.push(ev.data as any);
        };

        mediaRecorder.value.onstop = () => {
          attachedFile.value = null;
          blob.value = new Blob(chunks.value, {
            type: mediaRecorder.value?.mimeType || (chunks.value[0] as any).type || 'video/webm',
          });
          chunks.value = [];

          if (desktopRecordedVideoElement.value) {
            desktopRecordedVideoElement.value.src = window.URL.createObjectURL(blob.value);
            if (downloadableRecordedVideoElement.value) {
              downloadableRecordedVideoElement.value.href = desktopRecordedVideoElement.value?.src;
              downloadableRecordedVideoElement.value.download = 'RecordedVideo.webm';
            }
          }
        };
      }
    };

    async function getStream(): Promise<void> {
      const audioSource = selectedAudioDevice.value ?? audioInputDevices.value[0]?.deviceId;
      const videoSource = selectedVideoDevice.value ?? videoInputDevices.value[0]?.deviceId;
      const constraints = {
        ...(audioSource !== '' ? { audio: { deviceId: { exact: audioSource } } } : { audio: true }),
        ...(videoSource !== '' ? { video: { deviceId: { exact: videoSource } } } : { video: true }),
      };
      const stream = await getUserMedia(constraints);

      if (stream) {
        return attachStreamToVideoElement(stream);
      } else {
        return handleError('Unable to get a media stream');
      }
    }

    function buildConnectedDevicesDropdown(devices: MediaDeviceInfo[]) {
      (window as any).deviceInfos = devices; // make available to console
      if (process.env.NODE_ENV !== 'production') {
        console.log('Available input and output devices:', devices);
      }
      devices.map(({ kind, deviceId, label }) => {
        switch (kind) {
          case 'audioinput':
            audioInputDevices.value = [...audioInputDevices.value, { deviceId, label }];
            break;
          case 'videoinput':
            videoInputDevices.value = [...videoInputDevices.value, { deviceId, label }];
            break;
        }
      });
    }

    const initializeUserMedia = async () => {
      if (hasGetUserMedia()) {
        // getStream().then(getDevices).then(gotDevices);
        const stream = await getUserMedia();
        if (stream) {
          // We need to call gUM before gCD on safari, or it only shows the default devices
          const devices = await getConnectedDevices();
          buildConnectedDevicesDropdown(devices);
          attachStreamToVideoElement(stream);
        } else {
          return handleError('Unable to get a media stream');
        }
      } else {
        handleError('getUserMedia() is not supported by your browser');
      }
    };

    const handleStartRecording = (): void => {
      if (isRecording.value) {
        isPaused.value = false;
        mediaRecorder.value?.resume();
      } else {
        isRecording.value = true;
        isPaused.value = false;
        timer.start({
          precision: 'secondTenths',
          callback: (timer) => {
            elapsedTime.value = timer.getTimeValues().toString(['minutes', 'seconds']);
            if (Number(progress.value) >= 100) {
              return handleStopRecording();
            }
            if (questionInfo.value.max_duration) {
              progress.value =
                (Number(
                  `${timer.getTimeValues().minutes * 60 + timer.getTimeValues().seconds}.${
                    timer.getTimeValues().secondTenths
                  }`,
                ) *
                  100) /
                questionInfo.value.max_duration;
            }
          },
        });
        mediaRecorder.value?.start();
      }
      recordingStatus.value = mediaRecorder.value?.state ?? defaultRecordingState;
    };

    const handlePauseRecording = (): void => {
      startButtonVisible.value = false;
      resumeButtonVisible.value = true;
      restartButtonVisible.value = false;
      isRecording.value = true;
      isPaused.value = true;
      mediaRecorder.value?.pause();
      recordingStatus.value = mediaRecorder.value?.state ?? defaultRecordingState;
    };

    const handleStopRecording = (): void => {
      startButtonVisible.value = false;
      resumeButtonVisible.value = false;
      restartButtonVisible.value = true;
      isRecording.value = false;
      mediaRecorder.value?.stop();
      recordingStatus.value = mediaRecorder.value?.state ?? defaultRecordingState;
      videoPreviewVisible.value = true;
      timer.stop();
      revokeMediaAccess();
    };

    /* Upload video prop */
    const handleUploadRecording = async (): Promise<void> => {
      if (blob.value) {
        const file = new File(
          [blob.value],
          `altru-video-${questionInfo.value.id}-${Date.now()}${
            iframeSource.value ? `-${iframeSource.value}` : ''
          }.webm`,
        );
        return props.uploadVideo(file);
      }
      if (attachedFile.value) {
        return props.uploadVideo(attachedFile.value);
      } else {
        await dispatch('ui/setErrorMessage', { value: t('NO VIDEO FOUND') });
      }
    };

    /* Recording countdown */
    const countdownInterval = ref(setInterval(() => null));

    const startCountDownTimer = async (): Promise<void> => {
      showGuidelines.value = false;
      if (!(window as any).stream) {
        await getStream();
      }
      restartButtonVisible.value = false;
      settingsVisible.value = false;
      countDown.value = 3;
      countdownInterval.value = setInterval(() => {
        countDown.value -= 1;
      }, 1000);
    };

    const startNewRecording = async () => {
      await dispatch('ui/toggleLoading', { value: true });
      await getStream();
      await dispatch('ui/toggleLoading', { value: false });
      await startCountDownTimer();
    };

    watch(
      () => countDown.value,
      (current) => {
        videoPreviewVisible.value = false;
        if (current === 0) {
          clearInterval(countdownInterval.value);
          handleStartRecording();
        }
        if (current < 0) {
          clearInterval(countdownInterval.value);
        }
      },
      {
        immediate: true,
      },
    );

    const handleCancelRecordingCountdown = (): void => {
      countDown.value = -1;
      clearInterval(countdownInterval.value);
    };

    /* Devices list */
    const handleVideoDeviceChanged = (e: Event) => {
      selectedVideoDevice.value = (e.target as HTMLSelectElement).value;
      return getStream();
    };

    const handleAudioDeviceChanged = (e: Event) => {
      selectedAudioDevice.value = (e.target as HTMLSelectElement).value;
      return getStream();
    };

    /* Load video */
    const loadVideo = (e: Event): void | Promise<void> => {
      const files = (e.target as HTMLInputElement).files;
      const video: Ref<File> = ref(new File([], ''));
      if (files && files.length > 0) {
        blob.value = null;
        video.value = files[0];
        attachedFile.value = video.value;
        if (!isAcceptedVideoType(video.value.type)) {
          return dispatch('ui/setErrorMessage', {
            value: t('NOT SUPPORTED VIDEO FORMAT {0}', [acceptedVideoList]),
          });
        }
        if (desktopRecordedVideoElement.value) {
          desktopRecordedVideoElement.value.src = window.URL.createObjectURL(video.value);
          videoLoaded.value = true;
          videoPreviewVisible.value = true;
          restartButtonVisible.value = true;

          if (downloadableRecordedVideoElement.value) {
            downloadableRecordedVideoElement.value.href = desktopRecordedVideoElement.value?.src;
          }
        }
      }
    };

    /* Video overlay interactions */
    const settingsVisible = ref(false);
    const deviceOptions: Ref<HTMLDivElement | null> = ref(null);
    onClickOutside(deviceOptions, (e: Event) => {
      const elementType = (e.target as any).tagName;
      const elementClickedOnId = (e.target as any)?.id;
      if (elementType !== 'path' && elementType !== 'svg') {
        if (elementClickedOnId !== 'interactions-overlay-cog') {
          settingsVisible.value = false;
        }
      }
    });

    const toggleSettings = (e: Event): void => {
      e.stopPropagation();
      settingsVisible.value = !settingsVisible.value;
    };

    const fileLoader: Ref<HTMLInputElement | null> = ref(null);
    const handleAttachVideo = () => {
      showGuidelines.value = false;
      fileLoader.value?.click();
    };

    const acceptedVideoTypes = ACCEPTED_TYPES.join(',');
    const acceptedVideoList = USER_READABLE_ACCEPTED_TYPES.join(', ');

    return {
      videoContainer,
      desktopVideoElement,
      desktopRecordedVideoElement,
      videoLoadError,
      isPaused,
      countDown,
      videoPreviewVisible,
      videoInputDevices,
      audioInputDevices,
      isLoading,
      isRecording,
      videoLoaded,
      handlePauseRecording,
      handleStopRecording,
      handleUploadRecording,
      startCountDownTimer,
      startNewRecording,
      handleCancelRecordingCountdown,
      handleVideoDeviceChanged,
      handleAudioDeviceChanged,
      loadVideo,
      t,
      startButtonVisible,
      resumeButtonVisible,
      restartButtonVisible,
      toggleSettings,
      settingsVisible,
      deviceOptions,
      handleAttachVideo,
      fileLoader,
      elapsedTime,
      progress,
      formattedVideoMaxDuration,
      acceptedVideoTypes,
      downloadableRecordedVideoElement,
      userInformationNeeded,
      isIframed,
      showGuidelines,
      iframeParentIsAdminApp,
      clickWrapFeatureEnabled,
    };
  },
};
